import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import { Button, Table } from 'semantic-ui-react';
import cx from 'classnames';
import { defineMessages, injectIntl } from 'react-intl';

import Cell from '@plone/volto-slate/blocks/Table/Cell';
import TableSchema from '@plone/volto-slate/blocks/Table/schema';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import { BlockDataForm } from '@plone/volto/components/manage/Form';

import rowBeforeSVG from '@plone/volto/icons/row-before.svg';
import rowAfterSVG from '@plone/volto/icons/row-after.svg';
import colBeforeSVG from '@plone/volto/icons/column-before.svg';
import colAfterSVG from '@plone/volto/icons/column-after.svg';
import rowDeleteSVG from '@plone/volto/icons/row-delete.svg';
import colDeleteSVG from '@plone/volto/icons/column-delete.svg';
import mergeRightSVG from './icons/merge-right.svg';
import mergeDownSVG from './icons/merge-down.svg';
import unmergeSVG from './icons/unmerge.svg';

import {
  canMerge,
  canUnmerge,
  deleteColumn,
  deleteRow,
  findOrigin,
  insertColumn,
  insertRow,
  mergeCells,
  unmergeCells,
} from './mergeCells';

const getId = () => Math.floor(Math.random() * Math.pow(2, 24)).toString(32);

const getEmptyParagraph = () => [{ type: 'p', children: [{ text: '' }] }];

const initialTable = {
  hideHeaders: false,
  fixed: true,
  compact: false,
  basic: false,
  celled: true,
  inverted: false,
  striped: false,
  rows: [
    {
      key: getId(),
      cells: [
        { key: getId(), type: 'header', value: getEmptyParagraph() },
        { key: getId(), type: 'header', value: getEmptyParagraph() },
      ],
    },
    {
      key: getId(),
      cells: [
        { key: getId(), type: 'data', value: getEmptyParagraph() },
        { key: getId(), type: 'data', value: getEmptyParagraph() },
      ],
    },
  ],
};

const messages = defineMessages({
  insertRowBefore: {
    id: 'Insert row before',
    defaultMessage: 'Insert row before',
  },
  insertRowAfter: {
    id: 'Insert row after',
    defaultMessage: 'Insert row after',
  },
  deleteRow: {
    id: 'Delete row',
    defaultMessage: 'Delete row',
  },
  insertColBefore: {
    id: 'Insert col before',
    defaultMessage: 'Insert col before',
  },
  insertColAfter: {
    id: 'Insert col after',
    defaultMessage: 'Insert col after',
  },
  deleteCol: {
    id: 'Delete col',
    defaultMessage: 'Delete col',
  },
  sortableLimits: {
    id: 'Limits of the sortable table',
    defaultMessage:
      'Funciona só na leitura e ordena como texto: "10" vem antes de "9". Fica desligada enquanto houver células mescladas e, com os cabeçalhos ocultos, não há onde clicar para ordenar.',
  },
  mergeRight: {
    id: 'Merge with the cell on the right',
    defaultMessage: 'Mesclar com a célula à direita',
  },
  mergeDown: {
    id: 'Merge with the cell below',
    defaultMessage: 'Mesclar com a célula abaixo',
  },
  unmerge: {
    id: 'Undo cell merge',
    defaultMessage: 'Desfazer mesclagem',
  },
});

const TOOLBAR_OFFSET = 40;

class Edit extends Component {
  static propTypes = {
    data: PropTypes.objectOf(PropTypes.any).isRequired,
    detached: PropTypes.bool,
    index: PropTypes.number.isRequired,
    selected: PropTypes.bool.isRequired,
    block: PropTypes.string.isRequired,
    onAddBlock: PropTypes.func.isRequired,
    onChangeBlock: PropTypes.func.isRequired,
    onDeleteBlock: PropTypes.func.isRequired,
    onInsertBlock: PropTypes.func.isRequired,
    onMutateBlock: PropTypes.func.isRequired,
    onFocusPreviousBlock: PropTypes.func.isRequired,
    onFocusNextBlock: PropTypes.func.isRequired,
    onSelectBlock: PropTypes.func.isRequired,
  };

  static defaultProps = {
    detached: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      headers: [],
      rows: {},
      selected: {
        row: 0,
        cell: 0,
      },
      isClient: false,
      toolbarTop: TOOLBAR_OFFSET,
    };
    this.onSelectCell = this.onSelectCell.bind(this);
    this.updateToolbarTop = this.updateToolbarTop.bind(this);
    this.onInsertRowBefore = this.onInsertRowBefore.bind(this);
    this.onInsertRowAfter = this.onInsertRowAfter.bind(this);
    this.onInsertColBefore = this.onInsertColBefore.bind(this);
    this.onInsertColAfter = this.onInsertColAfter.bind(this);
    this.onDeleteRow = this.onDeleteRow.bind(this);
    this.onDeleteCol = this.onDeleteCol.bind(this);
    this.onChangeCell = this.onChangeCell.bind(this);
    this.onMergeRight = this.onMergeRight.bind(this);
    this.onMergeDown = this.onMergeDown.bind(this);
    this.onUnmerge = this.onUnmerge.bind(this);
  }

  componentDidMount() {
    if (!this.props.data.table || isEmpty(this.props.data.table)) {
      this.props.onChangeBlock(this.props.block, {
        ...this.props.data,
        table: initialTable,
      });
    }
    this.setState({ isClient: true });
    this.updateToolbarTop();
    window.addEventListener('resize', this.updateToolbarTop);
    window.addEventListener('scroll', this.updateToolbarTop, { passive: true });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateToolbarTop);
    window.removeEventListener('scroll', this.updateToolbarTop);
  }

  updateToolbarTop() {
    const topbar = document.querySelector('.mpf-topbar');
    const bottom = topbar ? topbar.getBoundingClientRect().bottom : 0;
    const toolbarTop = Math.max(bottom, 0) + TOOLBAR_OFFSET;
    if (toolbarTop !== this.state.toolbarTop) this.setState({ toolbarTop });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!nextProps.data.table || isEmpty(nextProps.data.table)) {
      this.props.onChangeBlock(nextProps.block, {
        ...nextProps.data,
        table: initialTable,
      });
    }
  }

  getSelectedOrigin() {
    const { selected } = this.state;
    if (!selected) return null;
    return findOrigin(
      this.props.data.table?.rows || [],
      selected.row,
      selected.cell,
    );
  }

  changeRows(rows) {
    this.props.onChangeBlock(this.props.block, {
      ...this.props.data,
      table: {
        ...this.props.data.table,
        rows,
      },
    });
  }

  onSelectCell(row, cell) {
    this.setState({ selected: { row, cell } });
  }

  onChangeCell(row, cell, slateValue) {
    const table = JSON.parse(JSON.stringify(this.props.data.table));
    table.rows[row].cells[cell] = {
      ...table.rows[row].cells[cell],
      value: JSON.parse(JSON.stringify(slateValue)),
    };
    this.props.onChangeBlock(this.props.block, {
      ...this.props.data,
      table,
    });
  }

  onInsertRowBefore() {
    const origin = this.getSelectedOrigin();
    if (!origin) return;
    this.changeRows(insertRow(this.props.data.table.rows, origin.row));
    this.setState({ selected: { row: origin.row + 1, cell: origin.cell } });
  }

  onInsertRowAfter() {
    const origin = this.getSelectedOrigin();
    if (!origin) return;
    const { rows } = this.props.data.table;
    const { rowSpan = 1 } = rows[origin.row].cells[origin.cell];
    this.changeRows(insertRow(rows, origin.row + rowSpan));
  }

  onInsertColBefore() {
    const origin = this.getSelectedOrigin();
    if (!origin) return;
    this.changeRows(
      insertColumn(this.props.data.table.rows, origin.cell, origin.cell),
    );
    this.setState({ selected: { row: origin.row, cell: origin.cell + 1 } });
  }

  onInsertColAfter() {
    const origin = this.getSelectedOrigin();
    if (!origin) return;
    const { rows } = this.props.data.table;
    const { colSpan = 1 } = rows[origin.row].cells[origin.cell];
    this.changeRows(insertColumn(rows, origin.cell + colSpan, origin.cell));
  }

  onDeleteCol() {
    const origin = this.getSelectedOrigin();
    if (!origin) return;
    const next = deleteColumn(this.props.data.table.rows, origin.cell);
    const lastCell = next[0].cells.length - 1;
    this.changeRows(next);
    this.setState({
      selected: findOrigin(next, origin.row, Math.min(origin.cell, lastCell)),
    });
  }

  onDeleteRow() {
    const origin = this.getSelectedOrigin();
    if (!origin) return;
    const next = deleteRow(this.props.data.table.rows, origin.row);
    this.changeRows(next);
    this.setState({
      selected: findOrigin(
        next,
        Math.min(origin.row, next.length - 1),
        origin.cell,
      ),
    });
  }

  onMergeRight() {
    const origin = this.getSelectedOrigin();
    if (!origin) return;
    this.changeRows(
      mergeCells(this.props.data.table.rows, origin.row, origin.cell, 'right'),
    );
    this.setState({ selected: origin });
  }

  onMergeDown() {
    const origin = this.getSelectedOrigin();
    if (!origin) return;
    this.changeRows(
      mergeCells(this.props.data.table.rows, origin.row, origin.cell, 'down'),
    );
    this.setState({ selected: origin });
  }

  onUnmerge() {
    const origin = this.getSelectedOrigin();
    if (!origin) return;
    this.changeRows(
      unmergeCells(this.props.data.table.rows, origin.row, origin.cell),
    );
    this.setState({ selected: origin });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selected && !this.props.selected) {
      this.setState({ selected: null });
    }
    if (!prevProps.selected && this.props.selected) {
      this.updateToolbarTop();
    }
  }

  render() {
    const tableRows = this.props.data.table?.rows;
    const headers = tableRows?.[0]?.cells || [];
    const rows = tableRows?.filter((_, index) => index > 0) || [];
    const baseSchema = TableSchema(this.props);
    const tableSchema = baseSchema.properties.table.schema;
    const schema = {
      ...baseSchema,
      properties: {
        ...baseSchema.properties,
        table: {
          ...baseSchema.properties.table,
          schema: {
            ...tableSchema,
            properties: {
              ...tableSchema.properties,
              sortable: {
                ...tableSchema.properties.sortable,
                title: (
                  <span
                    className="table-sortable-label"
                    title={this.props.intl.formatMessage(
                      messages.sortableLimits,
                    )}
                  >
                    {tableSchema.properties.sortable.title}
                  </span>
                ),
              },
            },
          },
        },
      },
    };
    const origin = this.getSelectedOrigin();

    return (
      <div className={cx('block table', { selected: this.props.selected })}>
        {this.props.selected && (
          <div
            className="table-toolbar-anchor"
            style={{ top: this.state.toolbarTop }}
          >
            <div className="toolbar">
              <Button.Group>
                <Button
                  icon
                  basic
                  onClick={this.onInsertRowBefore}
                  title={this.props.intl.formatMessage(
                    messages.insertRowBefore,
                  )}
                  aria-label={this.props.intl.formatMessage(
                    messages.insertRowBefore,
                  )}
                >
                  <Icon name={rowBeforeSVG} size="24px" />
                </Button>
              </Button.Group>
              <Button.Group>
                <Button
                  icon
                  basic
                  onClick={this.onInsertRowAfter}
                  title={this.props.intl.formatMessage(messages.insertRowAfter)}
                  aria-label={this.props.intl.formatMessage(
                    messages.insertRowAfter,
                  )}
                >
                  <Icon name={rowAfterSVG} size="24px" />
                </Button>
              </Button.Group>
              <Button.Group>
                <Button
                  icon
                  basic
                  onClick={this.onDeleteRow}
                  disabled={tableRows?.length === 1}
                  title={this.props.intl.formatMessage(messages.deleteRow)}
                  aria-label={this.props.intl.formatMessage(messages.deleteRow)}
                >
                  <Icon name={rowDeleteSVG} size="24px" />
                </Button>
              </Button.Group>
              <Button.Group>
                <Button
                  icon
                  basic
                  onClick={this.onInsertColBefore}
                  title={this.props.intl.formatMessage(
                    messages.insertColBefore,
                  )}
                  aria-label={this.props.intl.formatMessage(
                    messages.insertColBefore,
                  )}
                >
                  <Icon name={colBeforeSVG} size="24px" />
                </Button>
              </Button.Group>
              <Button.Group>
                <Button
                  icon
                  basic
                  onClick={this.onInsertColAfter}
                  title={this.props.intl.formatMessage(messages.insertColAfter)}
                  aria-label={this.props.intl.formatMessage(
                    messages.insertColAfter,
                  )}
                >
                  <Icon name={colAfterSVG} size="24px" />
                </Button>
              </Button.Group>
              <Button.Group>
                <Button
                  icon
                  basic
                  onClick={this.onDeleteCol}
                  disabled={tableRows?.[0].cells.length === 1}
                  title={this.props.intl.formatMessage(messages.deleteCol)}
                  aria-label={this.props.intl.formatMessage(messages.deleteCol)}
                >
                  <Icon name={colDeleteSVG} size="24px" />
                </Button>
              </Button.Group>
              <Button.Group>
                <Button
                  icon
                  basic
                  onClick={this.onMergeRight}
                  disabled={
                    !origin ||
                    !canMerge(tableRows, origin.row, origin.cell, 'right')
                  }
                  title={this.props.intl.formatMessage(messages.mergeRight)}
                  aria-label={this.props.intl.formatMessage(
                    messages.mergeRight,
                  )}
                >
                  <Icon name={mergeRightSVG} size="24px" />
                </Button>
              </Button.Group>
              <Button.Group>
                <Button
                  icon
                  basic
                  onClick={this.onMergeDown}
                  disabled={
                    !origin ||
                    !canMerge(tableRows, origin.row, origin.cell, 'down')
                  }
                  title={this.props.intl.formatMessage(messages.mergeDown)}
                  aria-label={this.props.intl.formatMessage(messages.mergeDown)}
                >
                  <Icon name={mergeDownSVG} size="24px" />
                </Button>
              </Button.Group>
              <Button.Group>
                <Button
                  icon
                  basic
                  onClick={this.onUnmerge}
                  disabled={
                    !origin || !canUnmerge(tableRows, origin.row, origin.cell)
                  }
                  title={this.props.intl.formatMessage(messages.unmerge)}
                  aria-label={this.props.intl.formatMessage(messages.unmerge)}
                >
                  <Icon name={unmergeSVG} size="24px" />
                </Button>
              </Button.Group>
            </div>
          </div>
        )}
        {this.props.data.table && (
          <Table
            fixed={this.props.data.table.fixed}
            compact={this.props.data.table.compact}
            basic={this.props.data.table.basic ? 'very' : false}
            celled={this.props.data.table.celled !== false}
            inverted={this.props.data.table.inverted}
            striped={this.props.data.table.striped}
            className="slate-table-block"
          >
            {!this.props.data.table.hideHeaders ? (
              <Table.Header>
                <Table.Row textAlign="left">
                  {headers.map((cell, cellIndex) =>
                    cell.merged ? null : (
                      <Table.HeaderCell
                        key={cell.key}
                        textAlign="left"
                        verticalAlign="middle"
                        colSpan={cell.colSpan}
                      >
                        <Cell
                          value={cell.value}
                          row={0}
                          cell={cellIndex}
                          onSelectCell={this.onSelectCell}
                          selected={
                            this.props.selected &&
                            this.state.selected &&
                            0 === this.state.selected.row &&
                            cellIndex === this.state.selected.cell
                          }
                          selectedCell={this.state.selected}
                          isTableBlockSelected={this.props.selected}
                          onAddBlock={this.props.onAddBlock}
                          onSelectBlock={this.props.onSelectBlock}
                          onChange={this.onChangeCell}
                          index={this.props.index}
                        />
                      </Table.HeaderCell>
                    ),
                  )}
                </Table.Row>
              </Table.Header>
            ) : (
              ''
            )}
            <Table.Body>
              {map(rows, (row, rowIndex) => (
                <Table.Row key={row.key}>
                  {map(row.cells, (cell, cellIndex) =>
                    cell.merged ? null : (
                      <Table.Cell
                        key={cell.key}
                        textAlign="left"
                        verticalAlign="middle"
                        colSpan={cell.colSpan}
                        rowSpan={cell.rowSpan}
                        className={
                          this.props.selected &&
                          this.state.selected &&
                          rowIndex + 1 === this.state.selected.row &&
                          cellIndex === this.state.selected.cell &&
                          this.props.selected
                            ? 'selected'
                            : ''
                        }
                      >
                        <Cell
                          value={cell.value}
                          row={rowIndex + 1}
                          cell={cellIndex}
                          onSelectCell={this.onSelectCell}
                          selected={
                            this.props.selected &&
                            this.state.selected &&
                            rowIndex + 1 === this.state.selected.row &&
                            cellIndex === this.state.selected.cell
                          }
                          selectedCell={this.state.selected}
                          isTableBlockSelected={this.props.selected}
                          onAddBlock={this.props.onAddBlock}
                          onSelectBlock={this.props.onSelectBlock}
                          onChange={this.onChangeCell}
                          index={this.props.index}
                        />
                      </Table.Cell>
                    ),
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
        {this.props.selected && this.state.selected && this.state.isClient && (
          <SidebarPortal selected={this.props.selected}>
            <BlockDataForm
              schema={schema}
              title={schema.title}
              onChangeField={(id, value) => {
                this.props.onChangeBlock(this.props.block, {
                  ...this.props.data,
                  [id]: value,
                });
              }}
              onChangeBlock={this.props.onChangeBlock}
              formData={{
                ...this.props.data,
                table: {
                  ...this.props.data.table,
                  celled: this.props.data.table?.celled !== false,
                },
              }}
              block={this.props.block}
              blocksConfig={this.props.blocksConfig}
            />
          </SidebarPortal>
        )}
      </div>
    );
  }
}

export default injectIntl(Edit);
